import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@core/http';
import { WebsocketLifecycleService } from '@core/realtime/websocket';
import { environment } from '@env';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { BehaviorSubject, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { monitorLogger } from '../monitor/monitor-logger';

const QR_SESSION_ID_STORAGE_KEY = 'qr_session_id';
const QR_SESSION_EVENT_TOKEN_STORAGE_KEY = 'qr_session_event_token';
const LEGACY_SESSION_STORAGE_KEY = 'webrtc_session_code';

type UploadErrorTranslateKey =
  | 'errors.400.upload.files'
  | 'errors.401.upload.files'
  | 'errors.403.upload.files'
  | 'errors.404.upload.files'
  | 'errors.409.upload.files'
  | 'errors.429.upload.files'
  | 'errors.500.upload.files'
  | 'errors.503.upload.files'
  | 'error.default.upload-files';

const UPLOAD_ERROR_TRANSLATE_KEYS: Record<number, UploadErrorTranslateKey> = {
  400: 'errors.400.upload.files',
  401: 'errors.401.upload.files',
  403: 'errors.403.upload.files',
  404: 'errors.404.upload.files',
  409: 'errors.409.upload.files',
  429: 'errors.429.upload.files',
  500: 'errors.500.upload.files',
  503: 'errors.503.upload.files',
};

export interface UploadedFile extends File {
  progress: number;
  qrSessionFileId?: string;
  qrSessionId?: string;
}

interface QrSessionFileResponse {
  file_id: string;
  name: string;
  size: number;
  mime_type: string;
  download_url: string;
}

interface CreateQrSessionResponse {
  session_id: string;
  expires_at: string;
  event_token: string;
}

interface QrSessionTicketResponse {
  ticket: string;
  expires_at: string;
}

interface QrSessionFilesResponse {
  session_id: string;
  expires_at: string;
  files: QrSessionFileResponse[];
}

interface QrSessionEvent {
  type: 'file_added' | 'file_removed' | 'session_closed';
  file?: QrSessionFileResponse;
  file_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UploadSessionService {
  private readonly baseUrl = `${environment.environmentVariables.apiCoreUrl}/Attachment/qr-session`;
  private readonly filesSubject = new BehaviorSubject<UploadedFile[]>([]);
  private readonly fileReceivedSubject = new Subject<void>();
  private eventSource: EventSource | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private sessionId: string | null = null;
  private eventToken: string | null = null;
  private isSessionClosed = false;
  private submittingQrSessionFileIds = new Set<string>();
  private submittedQrSessionFileIds = new Set<string>();
  private createdSessionIds = new Set<string>();

  public readonly files$ = this.filesSubject.asObservable();
  public readonly fileReceivedObservable$ = this.fileReceivedSubject.asObservable();

  constructor(
    private readonly apiService: ApiService,
    private readonly notificationService: NotificationsService,
    private readonly websocketLifecycleService: WebsocketLifecycleService,
  ) {
    localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
    this.sessionId = localStorage.getItem(QR_SESSION_ID_STORAGE_KEY);
    this.eventToken = localStorage.getItem(QR_SESSION_EVENT_TOKEN_STORAGE_KEY);
  }

  public startSession(): Observable<string> {
    const closeCurrentSession$ = this.sessionId
      ? this.apiService.delete<void>(`${this.baseUrl}/${this.sessionId}`).pipe(
          catchError(() => of(undefined)),
          tap(() => {
            this.filesSubject.next(this.filesSubject.value.filter((file) => !file.qrSessionFileId));
            this.clearSessionConnection();
          }),
        )
      : of(undefined);

    return closeCurrentSession$.pipe(
      tap(() => {
        this.closeEventStream();
        this.isSessionClosed = false;
        this.submittingQrSessionFileIds.clear();
        this.submittedQrSessionFileIds.clear();
        this.createdSessionIds.clear();
      }),
      switchMap(() => this.apiService.post<CreateQrSessionResponse>(this.baseUrl, null)),
      tap((session) => this.storeSession(session)),
      switchMap((session) => this.openEventStream().pipe(map(() => session.session_id))),
      catchError((error) => {
        this.notifyUploadError(error);
        return throwError(error);
      }),
    );
  }

  public resumeSession(): Observable<void> {
    if (!this.sessionId || !this.eventToken || this.eventSource) {
      return of(undefined);
    }

    this.isSessionClosed = false;
    return this.reconcileFiles().pipe(
      switchMap(() => this.openEventStream()),
      catchError((error) => {
        this.closeEventStream();
        this.notifyUploadError(error);
        return throwError(error);
      }),
    );
  }

  public addFile(file: File): void {
    const uploadedFile = Object.assign(file, { progress: 100 }) as UploadedFile;
    this.filesSubject.next([...this.filesSubject.value, uploadedFile]);
  }

  public getFiles(): UploadedFile[] {
    return this.filesSubject.value;
  }

  public clear(): void {
    this.filesSubject.next([]);
  }

  public removeFile(file: UploadedFile): void {
    this.filesSubject.next(this.filesSubject.value.filter((currentFile) => currentFile !== file));
  }

  public submitFilesForRequisition(chatId: string, requisitionId: string): Observable<void> {
    const files = this.getFiles();

    if (files.length === 0) {
      return of(undefined);
    }

    return this.uploadLegacyFiles(chatId, requisitionId, files).pipe(
      switchMap(() => this.cleanupSession()),
      tap(() => this.clear()),
      catchError((error) => {
        this.notifyUploadError(error);
        return throwError(error);
      }),
    );
  }

  public submitFilesForActiveRequisition(chatId: string, requisitionId: string): Observable<void> {
    const files = this.getFiles().filter((file) => this.shouldSubmitFile(file));

    if (files.length === 0) {
      return of(undefined);
    }

    this.markSubmittingSessionFiles(files);

    return this.uploadLegacyFiles(chatId, requisitionId, files).pipe(
      switchMap(() => this.removeSubmittedSessionFiles(files)),
      catchError((error) => {
        this.unmarkSubmittingSessionFiles(files);
        this.notifyUploadError(error);
        return throwError(error);
      }),
    );
  }

  public cleanupSession(preserveFiles = false): Observable<void> {
    const sessionIds = new Set(this.createdSessionIds);
    if (this.sessionId) {
      sessionIds.add(this.sessionId);
    }

    this.completeSession(preserveFiles);

    if (sessionIds.size === 0) {
      return of(undefined);
    }

    return forkJoin(
      Array.from(sessionIds).map((sessionId) =>
        this.apiService.delete<void>(`${this.baseUrl}/${sessionId}`).pipe(catchError(() => of(undefined))),
      ),
    ).pipe(map(() => undefined));
  }

  public expireSession(): void {
    this.closeEventStream();
  }

  public regenerateSession(): Observable<string> {
    this.closeEventStream();
    this.isSessionClosed = false;

    return this.apiService.post<CreateQrSessionResponse>(this.baseUrl, null).pipe(
      tap((session) => this.storeSession(session)),
      switchMap((session) => this.openEventStream().pipe(map(() => session.session_id))),
      catchError((error) => {
        this.notifyUploadError(error);
        return throwError(error);
      }),
    );
  }

  private storeSession(session: CreateQrSessionResponse): void {
    this.sessionId = session.session_id;
    this.eventToken = session.event_token;
    this.createdSessionIds.add(session.session_id);
    localStorage.setItem(QR_SESSION_ID_STORAGE_KEY, session.session_id);
    localStorage.setItem(QR_SESSION_EVENT_TOKEN_STORAGE_KEY, session.event_token);
  }

  private openEventStream(): Observable<void> {
    if (!this.sessionId || !this.eventToken) {
      return of(undefined);
    }

    const sessionId = this.sessionId;
    const eventToken = this.eventToken;
    const ticketUrl = `${this.baseUrl}/${sessionId}/events/ticket`;

    return this.apiService
      .post<QrSessionTicketResponse>(ticketUrl, { event_token: eventToken })
      .pipe(
        tap((response) => {
          const streamUrl = `${this.baseUrl}/${sessionId}/events?ticket=${encodeURIComponent(response.ticket)}`;
          this.eventSource = new EventSource(streamUrl);
          this.eventSource.onmessage = (event: MessageEvent) => this.handleEvent(event.data);
          this.eventSource.onerror = () => this.handleEventStreamError();
        }),
        map(() => undefined),
      );
  }

  private handleEvent(data: string): void {
    const event = JSON.parse(data) as QrSessionEvent;

    if (event.type === 'file_added' && event.file) {
      this.addSessionFile(event.file);
      return;
    }

    if (event.type === 'file_removed' && event.file_id) {
      this.filesSubject.next(this.filesSubject.value.filter((file) => file.qrSessionFileId !== event.file_id));
      return;
    }

    if (event.type === 'session_closed') {
      this.isSessionClosed = true;
      this.closeEventStream();
      this.filesSubject.next(this.filesSubject.value.filter((file) => file.qrSessionId !== this.sessionId));
      this.clearSessionConnection();
    }
  }

  private addSessionFile(file: QrSessionFileResponse): void {
    if (this.submittingQrSessionFileIds.has(file.file_id) || this.submittedQrSessionFileIds.has(file.file_id)) {
      return;
    }

    this.toUploadedFile(file, this.sessionId).subscribe({
      next: (uploadedFile) => {
        const currentFiles = this.filesSubject.value.filter(
          (currentFile) => currentFile.qrSessionFileId !== file.file_id,
        );
        this.filesSubject.next([...currentFiles, uploadedFile]);
        this.fileReceivedSubject.next();
        monitorLogger.screen.trackScreen('questionario_medico', 'enviado_arquivo_qr_code', {
          total_files: this.filesSubject.value.length,
        });
      },
      error: (error) => this.notifyUploadError(error),
    });
  }

  private handleEventStreamError(): void {
    this.closeEventStream();

    if (this.isSessionClosed || !this.sessionId) {
      return;
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconcileFiles()
        .pipe(switchMap(() => this.openEventStream()))
        .subscribe({
          error: (error) => {
            this.closeEventStream();
            this.notifyUploadError(error);
          },
        });
    }, 1000);
  }

  private reconcileFiles(): Observable<void> {
    const sessionId = this.sessionId;
    if (!sessionId) {
      return of(undefined);
    }

    return this.apiService.get<QrSessionFilesResponse>(`${this.baseUrl}/${sessionId}/files`).pipe(
      switchMap((response) => {
        const otherFiles = this.filesSubject.value.filter((file) => file.qrSessionId !== sessionId);
        if (response.files.length === 0) {
          this.filesSubject.next(otherFiles);
          return of(undefined);
        }

        const pendingSessionFiles = response.files.filter(
          (file) =>
            !this.submittingQrSessionFileIds.has(file.file_id) && !this.submittedQrSessionFileIds.has(file.file_id),
        );

        if (pendingSessionFiles.length === 0) {
          this.filesSubject.next(otherFiles);
          return of(undefined);
        }

        return forkJoin(pendingSessionFiles.map((file) => this.toUploadedFile(file, sessionId))).pipe(
          tap((sessionFiles) => this.filesSubject.next([...otherFiles, ...sessionFiles])),
          map(() => undefined),
        );
      }),
    );
  }

  private toUploadedFile(file: QrSessionFileResponse, sessionId: string | null): Observable<UploadedFile> {
    return this.apiService.getBlob(file.download_url, {}, true).pipe(
      map(
        (blob) =>
          Object.assign(new File([blob], file.name, { type: file.mime_type }), {
            progress: 100,
            qrSessionFileId: file.file_id,
            qrSessionId: sessionId,
          }) as UploadedFile,
      ),
    );
  }

  private uploadLegacyFiles(chatId: string, requisitionId: string, files: UploadedFile[]): Observable<void> {
    if (files.length === 0) {
      return of(undefined);
    }

    return forkJoin(
      files.map((file) => this.websocketLifecycleService.sendChatAttachment(chatId, file, { requisitionId })),
    ).pipe(map(() => undefined));
  }

  private removeSubmittedSessionFiles(files: UploadedFile[]): Observable<void> {
    const submittedFiles = new Set(files);

    files
      .filter((file) => file.qrSessionFileId)
      .forEach((file) => {
        this.submittingQrSessionFileIds.delete(file.qrSessionFileId);
        this.submittedQrSessionFileIds.add(file.qrSessionFileId);
      });

    this.filesSubject.next(this.filesSubject.value.filter((file) => !submittedFiles.has(file)));
    return of(undefined);
  }

  private shouldSubmitFile(file: UploadedFile): boolean {
    return (
      !file.qrSessionFileId ||
      (!this.submittingQrSessionFileIds.has(file.qrSessionFileId) &&
        !this.submittedQrSessionFileIds.has(file.qrSessionFileId))
    );
  }

  private markSubmittingSessionFiles(files: UploadedFile[]): void {
    files
      .filter((file) => file.qrSessionFileId)
      .forEach((file) => this.submittingQrSessionFileIds.add(file.qrSessionFileId));
  }

  private unmarkSubmittingSessionFiles(files: UploadedFile[]): void {
    files
      .filter((file) => file.qrSessionFileId)
      .forEach((file) => this.submittingQrSessionFileIds.delete(file.qrSessionFileId));
  }

  private completeSession(preserveFiles = false): void {
    this.closeEventStream();
    if (!preserveFiles) {
      this.clear();
    }
    this.clearSessionConnection();
    this.submittingQrSessionFileIds.clear();
    this.submittedQrSessionFileIds.clear();
    this.createdSessionIds.clear();
  }

  private clearSessionConnection(): void {
    localStorage.removeItem(QR_SESSION_ID_STORAGE_KEY);
    localStorage.removeItem(QR_SESSION_EVENT_TOKEN_STORAGE_KEY);
    this.sessionId = null;
    this.eventToken = null;
  }

  private closeEventStream(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private notifyUploadError(error?: unknown): void {
    const translateKey: UploadErrorTranslateKey =
      error instanceof HttpErrorResponse && UPLOAD_ERROR_TRANSLATE_KEYS[error.status]
        ? UPLOAD_ERROR_TRANSLATE_KEYS[error.status]
        : 'error.default.upload-files';

    this.notificationService.error({
      message: TranslateService.localize(translateKey),
    });
  }
}
